(() => {
  const syncPlayButton = (btn, video) => {
    const playing = !video.paused;
    btn.dataset.playing = playing ? 'true' : 'false';
    btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    const label = btn.querySelector('[data-curlear-showcase-play-label]');
    if (label) label.textContent = playing ? '❚❚' : '▶';
  };

  const syncMuteButton = (btn, video) => {
    const muted = video.muted;
    btn.dataset.muted = muted ? 'true' : 'false';
    btn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    const label = btn.querySelector('[data-curlear-showcase-mute-label]');
    if (label) label.textContent = muted ? '🔇' : '🔊';
  };

  const ensureLightbox = () => {
    let lightbox = document.querySelector('[data-curlear-showcase-lightbox]');
    if (lightbox) return lightbox;

    lightbox = document.createElement('div');
    lightbox.className = 'curlear-showcase-snippet__lightbox';
    lightbox.setAttribute('data-curlear-showcase-lightbox', '');
    lightbox.hidden = true;
    lightbox.innerHTML = `
      <button type="button" class="curlear-showcase-snippet__lightbox-close" data-curlear-showcase-lightbox-close aria-label="Close">×</button>
      <div class="curlear-showcase-snippet__lightbox-inner">
        <video class="curlear-showcase-snippet__lightbox-video" controls playsinline></video>
      </div>
    `;
    document.body.appendChild(lightbox);

    const close = () => {
      const video = lightbox.querySelector('video');
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      lightbox.hidden = true;
      document.documentElement.classList.remove('overflow-hidden');
    };

    lightbox.querySelector('[data-curlear-showcase-lightbox-close]').addEventListener('click', close);
    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) close();
    });
    document.addEventListener('keydown', (event) => {
      if (!lightbox.hidden && event.key === 'Escape') close();
    });

    return lightbox;
  };

  const openVideoLightbox = (sourceVideo) => {
    const lightbox = ensureLightbox();
    const lightboxVideo = lightbox.querySelector('video');
    const source =
      sourceVideo.currentSrc ||
      sourceVideo.src ||
      (sourceVideo.querySelector('source') && sourceVideo.querySelector('source').src);

    if (!source) return;

    lightboxVideo.src = source;
    lightboxVideo.muted = false;
    lightboxVideo.currentTime = sourceVideo.currentTime || 0;
    lightbox.hidden = false;
    document.documentElement.classList.add('overflow-hidden');
    lightboxVideo.play().catch(() => {});
  };

  const bindShowcaseVideos = (root) => {
    if (root.dataset.videoBound === 'true') return;
    root.dataset.videoBound = 'true';

    root.querySelectorAll('.curlear-showcase-snippet__media-wrap--video').forEach((wrap) => {
      const video = wrap.querySelector('video.curlear-showcase-snippet__media');
      if (!video) return;

      const playBtn = wrap.querySelector('[data-curlear-showcase-play]');
      const muteBtn = wrap.querySelector('[data-curlear-showcase-mute]');

      video.setAttribute('playsinline', '');
      video.playsInline = true;

      if (playBtn) {
        syncPlayButton(playBtn, video);
        playBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (video.paused) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
          syncPlayButton(playBtn, video);
        });
        video.addEventListener('play', () => syncPlayButton(playBtn, video));
        video.addEventListener('pause', () => syncPlayButton(playBtn, video));
      }

      if (muteBtn) {
        syncMuteButton(muteBtn, video);
        muteBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          video.muted = !video.muted;
          syncMuteButton(muteBtn, video);
        });
      }

      wrap.addEventListener('click', (event) => {
        if (event.target.closest('[data-curlear-showcase-play], [data-curlear-showcase-mute]')) return;
        event.preventDefault();
        openVideoLightbox(video);
      });
    });
  };

  const initShowcase = (root) => {
    if (root.dataset.showcaseInit === 'true') return;
    root.dataset.showcaseInit = 'true';

    const track = root.querySelector('[data-curlear-showcase-track]');
    const prev = root.querySelector('[data-curlear-showcase-prev]');
    const next = root.querySelector('[data-curlear-showcase-next]');
    if (!track) return;

    const isSlider = root.dataset.sliderActive === 'true';
    if (!isSlider) return;

    const step = () => {
      const first = track.querySelector('.curlear-showcase-snippet__item');
      if (!first) return 0;
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    };

    const move = (dir = 1) => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 1) return;
      const amount = step();
      if (amount <= 0) return;

      if (dir > 0 && track.scrollLeft >= maxScroll - 2) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      if (dir < 0 && track.scrollLeft <= 2) {
        track.scrollTo({ left: maxScroll, behavior: 'smooth' });
        return;
      }

      const nextPos = track.scrollLeft + amount * dir;
      track.scrollTo({ left: Math.max(0, Math.min(nextPos, maxScroll)), behavior: 'smooth' });
    };

    if (prev) prev.addEventListener('click', () => move(-1));
    if (next) next.addEventListener('click', () => move(1));

    const updateNavState = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const canScroll = maxScroll > 1;
      if (prev) {
        prev.classList.toggle('is-hidden', !canScroll || track.scrollLeft <= 2);
      }
      if (next) {
        next.classList.toggle('is-hidden', !canScroll);
      }
    };

    track.addEventListener('scroll', updateNavState, { passive: true });
    window.addEventListener('resize', updateNavState);
    updateNavState();

    const speed = parseInt(root.dataset.speed || '4000', 10);
    let timer = null;

    const startAuto = () => {
      clearInterval(timer);
      timer = setInterval(() => move(1), Math.max(2000, speed));
    };

    const stopAuto = () => clearInterval(timer);

    startAuto();
    root.addEventListener('mouseenter', stopAuto);
    root.addEventListener('mouseleave', startAuto);
    root.addEventListener('touchstart', stopAuto, { passive: true });
    root.addEventListener('touchend', startAuto, { passive: true });
  };

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-curlear-showcase]').forEach((root) => {
      bindShowcaseVideos(root);
      initShowcase(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('curlear:showcase:init', (event) => {
    const scope = (event.detail && event.detail.root) || document;
    boot(scope);
  });
})();
